import { useState, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { MainLayout } from '@/components/layout/MainLayout'
import { DiscoveryWorkflow } from '@/components/discovery/DiscoveryWorkflow'
import { Interviewer } from '@/components/interviewer/Interviewer'
import { OntologyExplorer } from '@/components/ontology-explorer/OntologyExplorer'
import { OntologyGraph } from '@/components/graph/OntologyGraph'
import { DataTreeView } from '@/components/tree/DataTreeView'
import { CanvasExplorer } from '@/components/canvas/CanvasExplorer'
import { CRMPage } from '@/components/crm/CRMPage'
import { PdfProcessor } from '@/components/pdf/PdfProcessor'
import { SettingsPage } from '@/components/settings/SettingsPage'
import { HelpPage } from '@/components/help/HelpPage'
import { SmartDocsPage } from '@/components/smart-docs'
import { Documents3Page } from '@/components/documents3/Documents3Page'
import { SourcesPage, SourceDetailView, WebSourcesPage } from '@/components/sources'
import { DiscoveryAssistantPage } from '@/components/discovery-assistant'
import { KnowledgeSourcesPage, KnowledgeExplorer, TrainingGuide } from '@/components/knowledge'
import { WorkspacePage } from '@/components/workspace/WorkspacePage'
import { LoginForm } from '@/components/auth/LoginForm'
import { RegisterForm } from '@/components/auth/RegisterForm'
import { ProjectSelector } from '@/components/projects/ProjectSelector'
import { useAuthStore } from '@/stores/authStore'
import { useProjectStore } from '@/stores/projectStore'
import { useInterviewerStore } from '@/stores/interviewerStore'
import { useDiscoveryStore } from '@/stores/discoveryStore'
import { useConnectionStore } from '@/stores/connectionStore'
import { setCurrentProject, setCurrentAccount } from '@/lib/api/sparql-adapter'
import { Loader2 } from 'lucide-react'

type AuthView = 'login' | 'register'

function App() {
  const [activeView, setActiveView] = useState('workspace')
  const [authView, setAuthView] = useState<AuthView>('login')
  const [showProjectSelector, setShowProjectSelector] = useState(false)
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null)
  const [knowledgeView, setKnowledgeView] = useState<'sources' | 'explorer' | 'guide'>('sources')

  const queryClient = useQueryClient()
  const previousProjectId = useRef<string | null>(null)

  const { isAuthenticated, isLoading: authLoading, checkAuth, currentAccount } = useAuthStore()
  const { currentProject, clearCurrentProject, clearProjects } = useProjectStore()
  const { initialize: initializeConnection, isConnected, autoConnect } = useConnectionStore()

  // Check authentication on startup
  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // Clear stale project and projects when not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      if (currentProject) clearCurrentProject()
      clearProjects()
    }
  }, [authLoading, isAuthenticated, currentProject, clearCurrentProject, clearProjects])

  // Sync current account and project to SPARQL adapter and manage per-project data
  useEffect(() => {
    const newProjectId = isAuthenticated && currentProject ? currentProject.id : null
    const oldProjectId = previousProjectId.current

    // Sync account and project to SPARQL adapter
    if (isAuthenticated && currentAccount) {
      setCurrentAccount(currentAccount.id)
    } else {
      setCurrentAccount(null)
    }

    if (isAuthenticated && currentProject) {
      setCurrentProject(currentProject.id)
    } else {
      setCurrentProject(null)
    }

    // If project changed (not just on initial load), save old data and load new
    if (oldProjectId !== null && oldProjectId !== newProjectId) {
      console.log(`Project changed from ${oldProjectId} to ${newProjectId} - switching data`)

      // Clear React Query cache to prevent stale data from previous project
      queryClient.clear()

      // Save current project's data to project-specific localStorage keys
      const interviewerData = localStorage.getItem('intellispot-interviewer')
      const discoveryData = localStorage.getItem('intellispot-discovery')

      if (interviewerData) {
        localStorage.setItem(`intellispot-interviewer-${oldProjectId}`, interviewerData)
      }
      if (discoveryData) {
        localStorage.setItem(`intellispot-discovery-${oldProjectId}`, discoveryData)
      }

      // Load new project's data (or clear if doesn't exist)
      if (newProjectId) {
        const newInterviewerData = localStorage.getItem(`intellispot-interviewer-${newProjectId}`)
        const newDiscoveryData = localStorage.getItem(`intellispot-discovery-${newProjectId}`)

        if (newInterviewerData) {
          localStorage.setItem('intellispot-interviewer', newInterviewerData)
          // Rehydrate the store from localStorage
          const parsed = JSON.parse(newInterviewerData)
          if (parsed.state) {
            useInterviewerStore.setState(parsed.state)
          }
        } else {
          // No data for new project - clear the store
          localStorage.removeItem('intellispot-interviewer')
          useInterviewerStore.setState({
            sources: [],
            currentSource: null,
            selectedFacetId: null,
          })
        }

        if (newDiscoveryData) {
          localStorage.setItem('intellispot-discovery', newDiscoveryData)
          // Rehydrate the store from localStorage
          const parsed = JSON.parse(newDiscoveryData)
          if (parsed.state) {
            useDiscoveryStore.setState(parsed.state)
          }
        } else {
          // No data for new project - reset
          localStorage.removeItem('intellispot-discovery')
          useDiscoveryStore.getState().resetIntake()
        }
      } else {
        // No project selected - clear stores
        localStorage.removeItem('intellispot-interviewer')
        localStorage.removeItem('intellispot-discovery')
        useInterviewerStore.setState({
          sources: [],
          currentSource: null,
          selectedFacetId: null,
        })
        useDiscoveryStore.getState().resetIntake()
      }
    }

    previousProjectId.current = newProjectId
  }, [isAuthenticated, currentAccount, currentProject, queryClient])

  // Auto-connect to Fuseki when project is selected and autoConnect is enabled
  useEffect(() => {
    if (isAuthenticated && currentProject && autoConnect && !isConnected) {
      // Small delay to ensure SPARQL adapter context is set
      const timer = setTimeout(() => {
        initializeConnection()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isAuthenticated, currentProject, autoConnect, isConnected, initializeConnection])

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-warm-100 rounded-2xl">
        <Loader2 className="w-8 h-8 text-solution animate-spin" />
      </div>
    )
  }

  // Show auth forms if not authenticated
  if (!isAuthenticated) {
    if (authView === 'login') {
      return <LoginForm onSwitchToRegister={() => setAuthView('register')} />
    }
    return <RegisterForm onSwitchToLogin={() => setAuthView('login')} />
  }

  // Show project selector if no project selected
  if (!currentProject || showProjectSelector) {
    return (
      <ProjectSelector
        onProjectSelected={() => setShowProjectSelector(false)}
      />
    )
  }

  const renderView = () => {
    switch (activeView) {
      case 'workspace':
        return <WorkspacePage />
      case 'discovery':
        return <DiscoveryWorkflow />
      case 'interviewer':
        return <Interviewer />
      case 'smartdocs':
        return <SmartDocsPage />
      case 'docs':
        return <Documents3Page />
      case 'crm':
        return <CRMPage />
      case 'knowledge':
        return (
          <div className="h-full flex flex-col">
            {/* Tab Navigation */}
            <div className="flex gap-1 p-2 bg-warm-100 border-b border-warm-200">
              <button
                onClick={() => setKnowledgeView('sources')}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  knowledgeView === 'sources'
                    ? 'bg-white shadow-sm font-medium text-text'
                    : 'text-text-secondary hover:bg-warm-200'
                }`}
              >
                Manage Sources
              </button>
              <button
                onClick={() => setKnowledgeView('explorer')}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  knowledgeView === 'explorer'
                    ? 'bg-white shadow-sm font-medium text-text'
                    : 'text-text-secondary hover:bg-warm-200'
                }`}
              >
                Ask Knowledge Base
              </button>
              <button
                onClick={() => setKnowledgeView('guide')}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  knowledgeView === 'guide'
                    ? 'bg-white shadow-sm font-medium text-market'
                    : 'text-text-secondary hover:bg-warm-200'
                }`}
              >
                Training Guide
              </button>
            </div>
            {/* Content */}
            <div className="flex-1 overflow-hidden">
              {knowledgeView === 'sources' && <KnowledgeSourcesPage />}
              {knowledgeView === 'explorer' && <KnowledgeExplorer />}
              {knowledgeView === 'guide' && <TrainingGuide />}
            </div>
          </div>
        )
      case 'ontology':
        return <OntologyExplorer />
      case 'schema':
      case 'instances':
      case 'query':
        // Advanced views now in Settings
        setActiveView('settings')
        return <SettingsPage />
      case 'graph':
        return <OntologyGraph />
      case 'tree':
        return <DataTreeView />
      case 'canvas':
        return <CanvasExplorer />
      case 'pdf':
        return <PdfProcessor onNavigateToSettings={() => setActiveView('settings')} />
      case 'settings':
        return <SettingsPage />
      case 'help':
        return <HelpPage />
      case 'sources':
        if (selectedSourceId) {
          return (
            <SourceDetailView
              sourceId={selectedSourceId}
              onBack={() => setSelectedSourceId(null)}
            />
          )
        }
        return (
          <SourcesPage
            onSelectSource={(id) => setSelectedSourceId(id)}
          />
        )
      case 'web':
        return <WebSourcesPage />
      case 'discovery-assistant':
        return (
          <DiscoveryAssistantPage
            onNavigateToSettings={() => setActiveView('settings')}
            onBack={() => setActiveView('discovery')}
          />
        )
      default:
        return null
    }
  }

  return (
    <MainLayout
      activeView={activeView}
      onViewChange={setActiveView}
      currentProject={currentProject}
      onSwitchProject={() => setShowProjectSelector(true)}
    >
      {renderView()}
    </MainLayout>
  )
}

export default App
